import React from 'react';
import Dialog from '../../components/Dialog';
import { ArrowLeft } from 'lucide-react';
import StepProgressBar from '../../components/StepProgressBar';
import MechanicalContractorBasicInfoForm from './MechanicalContractorBasicInfoForm';
import MechanicalContractorAddressInfoForm from './MechanicalContractorAddressInfoForm';
import { MechanicalContractorFormData, MechanicalContractorFormErrors, Province } from './dialogTypes';

interface MechanicalContractorDialogsProps {
  // Dialog states
  showAddMechanicalContractorDialog: boolean;
  showSaveConfirmDialog: boolean;
  showCloseConfirmDialog: boolean;
  isSubmitting: boolean;

  // Form data and steps
  formData: MechanicalContractorFormData;
  errors: MechanicalContractorFormErrors;
  currentStep: number;
  editFormData: any;

  // References
  addMechanicalContractorDialogContentRef: React.RefObject<HTMLDivElement>;

  // Options data
  provinceOptions: Province[];

  // Loading states
  isLoadingProvinces: boolean;

  // Deactivation eligibility
  canDeactivate?: boolean;
  deactivationReason?: string;

  // Status toggle visibility
  hideStatusToggle?: boolean;

  // Event handlers
  handleCloseAddMechanicalContractorDialog: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleSubOfficeChange: (index: number, field: string, value: string) => void;
  handleBack: () => void;
  handleNext: () => void;
  handleCancelSave: () => void;
  confirmSave: () => void;
  setShowCloseConfirmDialog: (show: boolean) => void;
  pendingCloseAction: null | (() => void);
  setFormData: React.Dispatch<React.SetStateAction<MechanicalContractorFormData>>;
}

const MechanicalContractorDialogs: React.FC<MechanicalContractorDialogsProps> = ({
  showAddMechanicalContractorDialog,
  showSaveConfirmDialog,
  showCloseConfirmDialog,
  isSubmitting,
  formData,
  errors,
  currentStep,
  editFormData,
  addMechanicalContractorDialogContentRef,
  provinceOptions,
  isLoadingProvinces,
  canDeactivate = true,
  deactivationReason = '',
  hideStatusToggle = false,
  handleCloseAddMechanicalContractorDialog,
  handleInputChange,
  handleSubOfficeChange,
  handleBack,
  handleNext,
  handleCancelSave,
  confirmSave,
  setShowCloseConfirmDialog,
  pendingCloseAction,
  setFormData
}) => {
  const steps = ['Basic Information', 'Address Information'];

  return (
    <>
      {/* Add Mechanical Contractor Dialog */}
      <Dialog
        id="add-mechanical-contractor-dialog"
        title={editFormData ? "Edit Mechanical Contractor" : "Add Mechanical Contractor"}
        isOpen={showAddMechanicalContractorDialog}
        onClose={handleCloseAddMechanicalContractorDialog}
        width="w-[95%] sm:w-[600px] md:w-[700px] lg:w-[500px]"
        padding="px-3 py-4 sm:px-4 sm:py-5 md:px-10 md:py-8 lg:px-6 lg:py-6"
      >
        {/* Add a max-height wrapper with scrolling */}
        <div ref={addMechanicalContractorDialogContentRef} className="max-h-[70vh] overflow-y-auto ">
          {/* Sticky Header Section */}
          <div className="sticky top-0 bg-white z-10">
            {/* Progress Steps */}
            <StepProgressBar currentStep={currentStep} steps={steps} />
          </div>

          {/* Form Content */}
          <div className="mt-6">
            {currentStep === 1 ? (
              <MechanicalContractorBasicInfoForm
                formData={formData}
                errors={errors}
                handleInputChange={handleInputChange}
                setFormData={setFormData}
                isEditMode={!!editFormData}
                canDeactivate={canDeactivate}
                deactivationReason={deactivationReason}
                hideStatusToggle={hideStatusToggle}
              />
            ) : (
              <MechanicalContractorAddressInfoForm
                formData={formData}
                errors={errors}
                handleInputChange={handleInputChange}
                handleSubOfficeChange={handleSubOfficeChange}
                provinceOptions={provinceOptions}
                isLoadingProvinces={isLoadingProvinces}
                setFormData={setFormData}
              />
            )}
          </div>

          {/* Navigation Buttons - Keep these fixed at the bottom */}
          <div className="sticky bottom-0 bg-white pt-8 mt-8 flex justify-between">
            {currentStep > 1 && (
              <button
                id="btn-previous"
                onClick={handleBack}
                className="btn-secondary z-9 flex items-center gap-2"
                disabled={isSubmitting}
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </button>
            )}
            <div className="flex-1" />
            <button
              id="btn-next"
              onClick={handleNext}
              className={`btn-primary z-9 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : currentStep === 2 ? (editFormData ? 'Save Changes' : 'Add Mechanical Contractor') : 'Next'}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Save Confirmation Dialog */}
      <Dialog
        id="save-mechanical-contractor-dialog"
        isOpen={showSaveConfirmDialog}
        onClose={isSubmitting ? () => {} : handleCancelSave}
        width="w-[450px]"
        padding="px-10 py-10"
        className="z-[60]"
        hideCloseButton={true}
        disableBackdropClick={true}
      >
        <div className="text-center">
          <h3 className="text-heading font-heading mb-2">Save Changes?</h3>
          <p className="text-text-secondary mb-8">Are you sure you want to save changes to this mechanical contractor?</p>

          <div className="flex justify-center gap-4">
            <button
              id="btn-save-no"
              onClick={handleCancelSave}
              className={`btn-secondary px-8 py-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isSubmitting}
            >
              No
            </button>
            <button
              id="btn-save-yes"
              onClick={confirmSave}
              className={`btn-primary px-8 py-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Yes'}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Close Confirmation Dialog */}
      <Dialog
        id="close-mechanical-contractor-dialog"
        isOpen={showCloseConfirmDialog}
        onClose={() => setShowCloseConfirmDialog(false)}
        width="w-[450px]"
        padding="px-10 py-10"
        className="z-[60]"
        hideCloseButton={true}
        disableBackdropClick={true}
      >
        <div className="text-center">
          <h3 className="text-heading font-heading mb-2">Discard Changes?</h3>
          <p className="text-text-secondary mb-8">Are you sure you want to discard your changes?</p>

          <div className="flex justify-center gap-4">
            <button
              id="btn-close-no"
              onClick={() => setShowCloseConfirmDialog(false)}
              className="btn-secondary px-8 py-2"
            >
              No
            </button>
            <button
              id="btn-close-yes"
              onClick={pendingCloseAction || (() => {})}
              className="btn-primary px-8 py-2"
            >
              Yes
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default MechanicalContractorDialogs;